import { useQuery } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { getQueryFn } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from "@/components/ui/form";
import { Loader2 } from "lucide-react";
import { billingModeValues, serviceItemUpsertSchema } from "@shared/contracts/commercial";

type BudgetOption = {
  id: string;
  name: string;
  client: { name: string };
};

type ServiceTypeOption = {
  id: string;
  label: string;
};

type FormValues = {
  budgetId: string;
  serviceTypeId: string;
  name: string;
  billingMode: (typeof billingModeValues)[number];
  isRecurringRevenue: boolean;
  requiresTask: boolean;
  defaultRate?: string;
  unitPrice?: string;
  quantity: string;
  notes?: string;
  isActive: boolean;
};

interface ServiceItemFormProps {
  defaultValues?: Partial<FormValues>;
  fixedBudgetId?: string;
  onSubmit: (data: Record<string, unknown>) => void;
  isPending?: boolean;
}

export function ServiceItemForm({
  defaultValues,
  fixedBudgetId,
  onSubmit,
  isPending,
}: ServiceItemFormProps) {
  const { data: budgets } = useQuery<BudgetOption[]>({
    queryKey: ["/api/budgets"],
    queryFn: getQueryFn({ on401: "throw" }),
  });

  const { data: serviceTypes } = useQuery<ServiceTypeOption[]>({
    queryKey: ["/api/service-types"],
    queryFn: getQueryFn({ on401: "throw" }),
  });

  const form = useForm<FormValues>({
    resolver: zodResolver(serviceItemUpsertSchema as any),
    defaultValues: {
      budgetId: fixedBudgetId ?? "",
      serviceTypeId: "",
      name: "",
      billingMode: "covered",
      isRecurringRevenue: false,
      requiresTask: false,
      defaultRate: "",
      unitPrice: "",
      quantity: "1",
      notes: "",
      isActive: true,
      ...defaultValues,
    },
  });

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit((values) =>
          onSubmit({
            ...values,
            quantity: Number(values.quantity || 1),
            defaultRate: values.defaultRate || null,
            unitPrice: values.unitPrice || null,
          }),
        )}
        className="space-y-4"
      >
        {!fixedBudgetId && (
          <FormField
            control={form.control}
            name="budgetId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Budget</FormLabel>
                <Select value={field.value} onValueChange={field.onChange}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select budget" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {(budgets ?? []).map((budget) => (
                      <SelectItem key={budget.id} value={budget.id}>
                        {budget.name} · {budget.client.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormItem>
            )}
          />
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          <FormField
            control={form.control}
            name="serviceTypeId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Service type</FormLabel>
                <Select value={field.value} onValueChange={field.onChange}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select service type" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {(serviceTypes ?? []).map((serviceType) => (
                      <SelectItem key={serviceType.id} value={serviceType.id}>
                        {serviceType.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Name</FormLabel>
                <FormControl>
                  <Input placeholder="Managed support delivery" {...field} />
                </FormControl>
              </FormItem>
            )}
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-4">
          <FormField
            control={form.control}
            name="billingMode"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Billing mode</FormLabel>
                <Select value={field.value} onValueChange={field.onChange}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {billingModeValues.map((value) => (
                      <SelectItem key={value} value={value}>
                        {value.replace(/_/g, " ")}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="defaultRate"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Default rate</FormLabel>
                <FormControl>
                  <Input type="number" step="0.01" {...field} value={field.value ?? ""} />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="unitPrice"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Unit price</FormLabel>
                <FormControl>
                  <Input type="number" step="0.01" {...field} value={field.value ?? ""} />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="quantity"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Quantity</FormLabel>
                <FormControl>
                  <Input type="number" min="1" {...field} />
                </FormControl>
              </FormItem>
            )}
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-3">
          <FormField
            control={form.control}
            name="isRecurringRevenue"
            render={({ field }) => (
              <FormItem className="flex items-center gap-3">
                <FormControl>
                  <Switch checked={field.value} onCheckedChange={field.onChange} />
                </FormControl>
                <FormLabel>Recurring revenue</FormLabel>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="requiresTask"
            render={({ field }) => (
              <FormItem className="flex items-center gap-3">
                <FormControl>
                  <Switch checked={field.value} onCheckedChange={field.onChange} />
                </FormControl>
                <FormLabel>Requires task</FormLabel>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="isActive"
            render={({ field }) => (
              <FormItem className="flex items-center gap-3">
                <FormControl>
                  <Switch checked={field.value} onCheckedChange={field.onChange} />
                </FormControl>
                <FormLabel>Active</FormLabel>
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="notes"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Notes</FormLabel>
              <FormControl>
                <Textarea {...field} value={field.value ?? ""} className="min-h-[80px]" />
              </FormControl>
            </FormItem>
          )}
        />

        <div className="flex justify-end">
          <Button type="submit" disabled={isPending}>
            {isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Save service item
          </Button>
        </div>
      </form>
    </Form>
  );
}
